import { ChangeEvent, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { useUser } from '@clerk/clerk-react';
import { MenuItem, Stack, TextField, Typography } from '@mui/material';
import { SCHEDULE_OPTIONS, TIMEOUT_OPTIONS } from 'constants/scheduleOptions';
import { Agent } from 'types/agents';
import { Instruction, Schedule, ScheduleAt } from 'types/task';

import { InstructionForm } from 'components/Instructions/InstructionForm';

import { useApi } from '../../hooks/useApi';

interface IInstructionsProps {
  agents: Agent[];
}

const DEFAULT_VALUES: Instruction = {
  instruction: '',
  schedule: Schedule.NOW,
  agentId: '',
  instructionName: '',
  scheduleAt: {
    weekday_number: null,
    schedule_time: null,
    month_day: null,
  },
};

export const Instructions = ({ agents }: IInstructionsProps) => {
  const [instruction, setInstruction] = useState({ ...DEFAULT_VALUES });
  const [loading, setLoading] = useState(false);
  const [isError, setIsError] = useState(false);
  const [savedInstructions, setSavedInstructions] = useState([]);
  const [selectedInstruction, setSelectedInstruction] = useState('');
  const { user } = useUser();
  const { apiFetch } = useApi();

  const fetchInstructions = async () => {
    const res = await apiFetch('instructions');
    const instructions = await res.json();

    setSavedInstructions(instructions);
  };

  useEffect(() => {
    if (user?.id) {
      fetchInstructions();
    }
  }, [user?.id]);

  const selectSavedInstruction = (e: ChangeEvent<HTMLInputElement>) => {
    const id = e.target.value;
    setSelectedInstruction(id);

    const inst = savedInstructions.find((ins) => ins.id === id) || DEFAULT_VALUES;

    setInstruction({ ...instruction, instructionName: inst?.instruction_name, ...inst });
  };

  const handleSubmit = async () => {
    if (!instruction.agentId || !instruction.instruction) {
      setIsError(true);
      return;
    }

    setIsError(false);
    setLoading(true);

    try {
      const response = await apiFetch('schedule', {
        method: 'POST',
        body: JSON.stringify({ ...instruction, date: new Date().toISOString() }),
      });

      if (response.ok) {
        toast.success('Instructions scheduled successfully!', {
          position: 'top-right',
          autoClose: 5000,
        });

        fetchInstructions();
        setInstruction({ ...DEFAULT_VALUES });
      } else {
        toast.error('Failed to schedule instruction.', {
          position: 'top-right',
          autoClose: 5000,
        });
      }
    } catch (e) {
      toast.error('Something went wrong.', {
        position: 'top-right',
        autoClose: 5000,
      });
    } finally {
      setLoading(false);
    }
  };

  const submitInstruction = async () => {
    handleSubmit();
  };

  const changeInstruction = (
    key: keyof Instruction,
    value: string | number | Schedule | ScheduleAt
  ) => {
    setInstruction({ ...instruction, [key]: value });
  };

  return (
    <Stack gap={4}>
      <Stack gap={2}>
        <Typography variant="h5">Select Agent</Typography>
        <TextField
          select
          fullWidth
          onChange={(e) => changeInstruction('agentId', e.target.value)}
          value={instruction.agentId}
          variant="outlined"
          error={isError && !instruction.agentId}
        >
          <MenuItem value="">Select an agent</MenuItem>

          {agents?.map((agent) => (
            <MenuItem
              key={agent.id}
              value={agent.id}
            >
              {agent.name}
            </MenuItem>
          ))}
        </TextField>
      </Stack>
      {!!savedInstructions?.length && (
        <Stack gap={2}>
          <Typography variant="h5">Saved Instructions</Typography>
          <TextField
            select
            fullWidth
            value={selectedInstruction}
            onChange={selectSavedInstruction}
            variant="outlined"
          >
            <MenuItem value="">Select an instruction</MenuItem>

            {savedInstructions.map((instruction) => (
              <MenuItem
                key={instruction.id}
                value={instruction.id}
              >
                {instruction.instruction_name}
              </MenuItem>
            ))}
          </TextField>
        </Stack>
      )}
      <Stack gap={2}>
        <Typography variant="h5">Instruction Name (Tag)</Typography>
        <TextField
          fullWidth
          onChange={(e) => changeInstruction('instructionName', e.target.value)}
          value={instruction.instructionName}
          variant="outlined"
          error={isError && !instruction.instructionName}
        />
      </Stack>
      <InstructionForm
        buttonTitle="Set Instruction"
        onChangeSchedule={changeInstruction}
        schedule={instruction.schedule}
        value={instruction.instruction}
        instruction={instruction}
        onChange={changeInstruction}
        isLoading={loading}
        onSubmit={submitInstruction}
        title="Set Instruction"
        scheduleOptions={[...TIMEOUT_OPTIONS, ...SCHEDULE_OPTIONS]}
      />
    </Stack>
  );
};
